const STORAGE_PREFIX = "moo_payment_draft_request_v1";
const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000;

const normalizeNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const normalizeExtras = (extras) =>
  (Array.isArray(extras) ? extras : [])
    .map((extra) => ({
      id: String(extra?.id ?? extra?.extra_id ?? extra?.product_id ?? ""),
      name: String(extra?.name ?? extra?.label ?? ""),
      price: normalizeNumber(extra?.price ?? extra?.unit_price),
      quantity: normalizeNumber(
        extra?.selectedQuantity ?? extra?.quantity ?? extra?.qty ?? 1,
      ),
    }))
    .sort((left, right) =>
      JSON.stringify(left).localeCompare(JSON.stringify(right)),
    );

const canonicalDraftItems = (items) =>
  (Array.isArray(items) ? items : [])
    .map((item) => ({
      clientLineId: String(
        item?.clientLineId ?? item?.client_line_id ?? item?.lineKey ?? "",
      ),
      productId: String(item?.productId ?? item?.product_id ?? item?.id ?? ""),
      variationId: String(
        item?.variationId ?? item?.variation_id ?? item?.selectedVariationId ?? "",
      ),
      quantity: normalizeNumber(item?.quantity ?? item?.qty ?? 1),
      unitPrice: normalizeNumber(
        item?.unitPrice ?? item?.unit_price ?? item?.price ?? item?.product_price,
      ),
      extras: normalizeExtras(item?.extras),
    }))
    .sort((left, right) => {
      const leftKey = `${left.clientLineId}:${left.productId}:${left.variationId}`;
      const rightKey = `${right.clientLineId}:${right.productId}:${right.variationId}`;
      return leftKey.localeCompare(rightKey);
    });

const hashString = (value) => {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
};

export const buildPaymentDraftFingerprint = ({ scopeKey, items }) =>
  hashString(
    JSON.stringify({
      scopeKey: String(scopeKey || "unknown"),
      items: canonicalDraftItems(items),
    }),
  );

const storageKeyForScope = (scopeKey) =>
  `${STORAGE_PREFIX}:${encodeURIComponent(String(scopeKey || "unknown"))}`;

const fallbackRequestId = () =>
  `checkout-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const getOrCreatePaymentDraftRequest = ({
  storage,
  scopeKey,
  items,
  now = Date.now(),
  ttlMs = DEFAULT_TTL_MS,
  randomUUID,
}) => {
  const key = storageKeyForScope(scopeKey);
  const fingerprint = buildPaymentDraftFingerprint({ scopeKey, items });
  try {
    const existing = JSON.parse(storage?.getItem(key) || "null");
    if (
      existing?.requestId &&
      existing?.fingerprint === fingerprint &&
      Number(existing?.expiresAt) > now
    ) {
      return { ...existing, storageKey: key, reused: true };
    }
  } catch (_error) {
    // Une entrée locale corrompue ne doit jamais bloquer l'encaissement.
  }

  const uuidFactory =
    randomUUID ||
    (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? () => crypto.randomUUID()
      : fallbackRequestId);
  const record = {
    requestId: `checkout-order-${uuidFactory()}`,
    fingerprint,
    createdAt: now,
    expiresAt: now + ttlMs,
  };
  try {
    storage?.setItem(key, JSON.stringify(record));
  } catch (_error) {
    // Le serveur reste idempotent pendant la vie du modal grâce à la ref React.
  }
  return { ...record, storageKey: key, reused: false };
};

export const clearPaymentDraftRequest = ({ storage, storageKey }) => {
  if (!storageKey) return;
  try {
    storage?.removeItem(storageKey);
  } catch (_error) {
    // Sans effet fonctionnel : l'entrée expirera et son empreinte empêchera
    // qu'elle soit réutilisée pour un panier différent.
  }
};

